import {
  CONTACT_OPTION_NAME,
  CONTACT_PREFERENCE_IDS,
  PAPERLESS_PREFERENCE_IDS,
  TOGGLE_OPTION_NAME,
} from 'common/communication-preferences.constants';
import {
  buildPreferenceDelta,
  type CommunicationPreferencesPatch,
  type PreferenceDeltaEntry,
} from 'common/communication-preferences.api';

// Contact fields the member supplied on the Account Setup form. `undefined`
// means "not supplied" — the field is omitted from the PATCH so the back end's
// JSON Merge Patch leaves it untouched. A string (including '') means "write
// this value", where '' clears the field — matching how the Settings
// Communication Preferences screen sends contact values through the same
// resolver.
export type AccountSetupContactFields = {
  mobilePhone?: string;
  preferredPhone?: string;
  email?: string;
  // Go Paperless rides in the same PATCH as the contact details it depends on,
  // so the opt-in and the email it needs can't land half-applied (GEIS-579).
  // Only ever `true`; omitted when the member leaves the box unchecked.
  goPaperless?: true;
};

// Build the JSON:API preference delta for the contact section from the supplied
// Account Setup fields (GEIS-500). Pure — the hook owns the network call.
export const buildContactInformationDelta = (
  fields: AccountSetupContactFields,
): CommunicationPreferencesPatch => {
  const entries: PreferenceDeltaEntry[] = [];

  const addEntry = (id: string, value: string) => {
    entries.push({ id, optionName: CONTACT_OPTION_NAME, value });
  };

  if (fields.mobilePhone !== undefined) {
    addEntry(CONTACT_PREFERENCE_IDS.mobilePhone, fields.mobilePhone);
  }
  if (fields.preferredPhone !== undefined) {
    addEntry(CONTACT_PREFERENCE_IDS.preferredPhone, fields.preferredPhone);
  }
  if (fields.email !== undefined) {
    addEntry(CONTACT_PREFERENCE_IDS.email, fields.email);
  }
  // S-5 couples the master toggle to the documents it covers, so the opt-in
  // sends the whole set — the master alone would read back as off in Settings.
  if (fields.goPaperless) {
    PAPERLESS_PREFERENCE_IDS.forEach((id) => {
      entries.push({ id, optionName: TOGGLE_OPTION_NAME, value: 'true' });
    });
  }

  return buildPreferenceDelta(entries);
};
